import React, {
  ComponentProps,
  forwardRef,
  ReactNode,
  Ref,
  useLayoutEffect,
  useRef,
} from 'react';

import PropTypes from 'prop-types';

import { useTheme } from '@adsk/alloy-react-styles';
import {
  StylableComponent,
  StyleProp,
  stylePropType,
} from '@adsk/alloy-react-theme';

import Tree from '../../Tree';
import { DropdownTreeGetMenuProps, IdType } from '../../types';

type VirtualizedProps = {
  isVirtualized: true;
  parentRef: Ref<HTMLDivElement>;
  totalSize: number;
};

type NonVirtualizedProps = {
  isVirtualized?: never;
  parentRef?: Ref<HTMLDivElement>;
  totalSize?: number;
};

type DropdownTreeProps = Omit<ComponentProps<typeof Tree>, 'as'> &
  (VirtualizedProps | NonVirtualizedProps) & {
    ['data-testid']?: string;
    isOpen: boolean;
    getMenuProps: DropdownTreeGetMenuProps;
    renderFooter?: (props?: StylableComponent<HTMLDivElement>) => ReactNode;
    getFooterProps?: () => StylableComponent<HTMLDivElement>;
    highlightedId?: IdType;
  };

const heightStyle: StyleProp = {
  maxHeight: 300,
  overflow: 'auto',
};

type InnerTreeProps = ComponentProps<typeof Tree> & {
  width?: number;
  totalSize?: number;
  onKeyDown?: React.KeyboardEventHandler<HTMLUListElement>;
  isOpen: boolean;
};

type VirtualizedTreeProps = Omit<InnerTreeProps, 'isVirtualized' | 'ref'> & {
  overlayStyle?: StyleProp;
};

const InnerTree: React.FC<InnerTreeProps> = ({
  style,
  isOpen,
  children,
  width,
  ...props
}) => {
  const { tokens } = useTheme();
  const dropdown = tokens?.component.tree?.dropdown;

  return (
    <Tree style={[{ ...dropdown?.inner, width }, style]} {...props}>
      {isOpen ? children : <></>}
    </Tree>
  );
};

const VirtualizedTree = forwardRef<HTMLDivElement, VirtualizedTreeProps>(
  ({ isOpen, totalSize, overlayStyle, children, ...props }, ref) => (
    <div
      ref={ref}
      css={[
        {
          height: isOpen ? undefined : 0,
        },
        heightStyle,
        overlayStyle,
      ]}
    >
      <InnerTree
        isOpen={isOpen}
        style={{
          position: 'relative',
          height: totalSize,
          width: '100%',
          margin: 0,
        }}
        {...props}
      >
        {children}
      </InnerTree>
    </div>
  ),
);
VirtualizedTree.displayName = 'VirtualizedTree';

const DropdownTree = forwardRef<HTMLUListElement, DropdownTreeProps>(
  (
    {
      ['data-testid']: dataTestid,
      isOpen,
      getMenuProps,
      children,
      style: styleProp,
      onKeyDown,
      isVirtualized,
      parentRef,
      totalSize,
      renderFooter,
      getFooterProps,
      highlightedId,
      ...props
    },
    ref,
  ) => {
    const {
      style: overlayStyle,
      width,
      onKeyDown: _ignored,
      ref: menuRef,
      ...menuProps
    } = getMenuProps({ ...props, ref });

    const prevIsOpenRef = useRef(isOpen);

    useLayoutEffect(() => {
      if (isOpen && !prevIsOpenRef.current && highlightedId !== undefined) {
        const element = document.getElementById(highlightedId.toString());
        if (element) {
          element.scrollIntoView({ behavior: 'auto', block: 'nearest' });
        }
      }
      prevIsOpenRef.current = isOpen;
    }, [isOpen, highlightedId]);

    const { tokens } = useTheme();
    const containerStyle = tokens?.component.tree?.dropdown?.container;

    return (
      <div
        ref={menuRef as React.ForwardedRef<HTMLDivElement>}
        css={[containerStyle, styleProp]}
      >
        <div css={{ ...(!isVirtualized && heightStyle) }}>
          {isVirtualized ? (
            <VirtualizedTree
              ref={parentRef}
              data-testid={dataTestid}
              width={width}
              overlayStyle={overlayStyle}
              totalSize={totalSize}
              onKeyDown={onKeyDown}
              isOpen={isOpen}
              {...menuProps}
            >
              {children}
            </VirtualizedTree>
          ) : (
            <InnerTree
              data-testid={dataTestid}
              width={width}
              onKeyDown={onKeyDown}
              isOpen={isOpen}
              style={overlayStyle}
              {...menuProps}
            >
              {children}
            </InnerTree>
          )}
        </div>
        {isOpen && renderFooter?.(getFooterProps?.())}
      </div>
    );
  },
);

DropdownTree.displayName = 'DropdownTree';

DropdownTree.propTypes = {
  style: stylePropType,
  className: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  children: PropTypes.any,
  onKeyDown: PropTypes.func,
};

export default DropdownTree;
